/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, MapPin, Compass, X } from 'lucide-react';
import {
  INDIA_STATES, SRI_LANKA_OUTLINE, INDIA_BOUNDS,
  projectLatLng, pointsToPath, stateToPath,
} from './india_geo';

const VIEW_W = 1000;
const VIEW_H = 1100;

/**
 * Territory Map — Phase 5.2.
 *
 * Stewardship canvas: a static SVG India outline (Gujarat highlighted),
 * centre pins with a Stabilization Pulse™ ring, drilldown side-panel,
 * and an "Unmapped Centres" section for centres without coordinates yet.
 *
 * No external map tiles. No sales-language. Read-only.
 */
export default function TerritoryMapPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/terra-growth/territory-map?days=7`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="territory-map-page" className="terra-page-pad max-w-7xl mx-auto px-8 py-12">
        <Header />

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-16 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Composing the canvas…
          </div>
        ) : !data ? (
          <div data-testid="territory-map-error"
               className="rounded-2xl border border-[#EBE8E3] bg-white p-16 text-center text-[#8A8681]">
            Could not compose the territory canvas right now.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Canvas centres={data.centres || []}
                      onSelect={setSelected}
                      selected={selected} />
              <CanvasLegend mapped={data.mapped_count}
                            total={data.total} />
            </div>
            <aside className="space-y-6">
              {selected ? (
                <Drilldown centre={selected} onClose={() => setSelected(null)} />
              ) : (
                <ViewerHint mapped={data.mapped_count} />
              )}
              <UnmappedPanel
                centres={data.unmapped_centres || []}
                onSelect={setSelected}
                selected={selected} />
            </aside>
          </div>
        )}

        <p className="text-[11px] text-[#8A8681] italic max-w-2xl mt-12 pt-6 border-t border-[#F4F2EE]">
          {data?.philosophy_note ||
           'Territory stewardship canvas — every centre is calmly held.'}
        </p>
      </div>
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Header
// ═══════════════════════════════════════════════════════════════════
function Header() {
  return (
    <header className="mb-10 max-w-2xl space-y-3">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C6239] flex items-center gap-2">
        <Compass className="w-3 h-3" /> Terra Growth OS™ · Visualization
      </p>
      <h1 className="text-4xl font-['Outfit'] font-light text-[#2C2A29] tracking-tight">
        Territory Map
      </h1>
      <p className="text-sm text-[#8A8681] font-['Work_Sans'] leading-relaxed">
        A calm canvas of the territory we steward. Each centre breathes its
        own Stabilization Pulse™ — the wider the ring, the clearer the
        rhythm. Click a pin to listen in.
      </p>
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SVG Canvas
// ═══════════════════════════════════════════════════════════════════

function Canvas({ centres, onSelect, selected }) {
  const statePaths = useMemo(
    () => INDIA_STATES.map(s => ({
      ...s,
      d: stateToPath(s, VIEW_W, VIEW_H, INDIA_BOUNDS),
    })),
    [],
  );
  const lankaPath = useMemo(
    () => pointsToPath(SRI_LANKA_OUTLINE, VIEW_W, VIEW_H, INDIA_BOUNDS), []);

  return (
    <section data-testid="territory-canvas"
             className="rounded-2xl border border-[#D8CFB8] bg-[#FBF6E9] p-4 overflow-hidden shadow-[inset_0_0_60px_rgba(140,98,57,0.06),0_1px_2px_rgba(31,29,27,0.04)]">
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
           className="w-full h-auto select-none"
           role="img" aria-label="Territory map of India — Survey-of-India aligned">
        {/* Subtle parchment grid */}
        <defs>
          <pattern id="terra-grid" x="0" y="0" width="50" height="50"
                   patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#D8CFB8"
                  strokeWidth="0.5" opacity="0.45" />
          </pattern>
          <pattern id="terra-grain" x="0" y="0" width="3" height="3"
                   patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.4" fill="#8C6239" opacity="0.06" />
          </pattern>
          <radialGradient id="pulse-flowing" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4A5D4E" stopOpacity="0.18" />
            <stop offset="80%" stopColor="#4A5D4E" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#4A5D4E" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="pulse-stewarded" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7A8A8C" stopOpacity="0.15" />
            <stop offset="80%" stopColor="#7A8A8C" stopOpacity="0.04" />
            <stop offset="100%" stopColor="#7A8A8C" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="pulse-softening" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C8A368" stopOpacity="0.16" />
            <stop offset="80%" stopColor="#C8A368" stopOpacity="0.04" />
            <stop offset="100%" stopColor="#C8A368" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="pulse-quiet" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#A8A29E" stopOpacity="0.12" />
            <stop offset="80%" stopColor="#A8A29E" stopOpacity="0.03" />
            <stop offset="100%" stopColor="#A8A29E" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width={VIEW_W} height={VIEW_H} fill="url(#terra-grid)"
              pointerEvents="none" />
        <rect width={VIEW_W} height={VIEW_H} fill="url(#terra-grain)"
              pointerEvents="none" />

        {/* Sri Lanka — calm reference (rendered first, behind India) */}
        <path d={lankaPath} fill="#F2EAD8" stroke="#C8A368"
              strokeWidth="1.0" opacity="0.45" pointerEvents="none" />

        {/* India — every state as its own path. Subtle sage internal
            hairlines + warm copper outer outline. Survey-of-India aligned. */}
        <g pointerEvents="none">
          {statePaths.map(s => {
            const isGujarat = s.code === 'GUJARAT';
            return (
              <path
                key={s.code}
                d={s.d}
                fill={isGujarat ? '#B5C2A8' : '#F4ECD7'}
                stroke={isGujarat ? '#4A5D4E' : '#B8AC8E'}
                strokeWidth={isGujarat ? 1.4 : 0.7}
                strokeLinejoin="round"
                opacity={isGujarat ? 0.78 : (s.is_island ? 0.62 : 0.74)}
              />
            );
          })}
          {/* Outer copper outline traced once over everything for
              definition, no fill so internal hairlines stay visible. */}
          {statePaths.map(s => (
            <path
              key={`o-${s.code}`}
              d={s.d}
              fill="none"
              stroke="#A98660"
              strokeWidth="0.4"
              opacity="0.55"
            />
          ))}
        </g>

        {/* Compass rose — faint, top-right */}
        <g transform={`translate(${VIEW_W - 90} 90)`} opacity="0.32"
           pointerEvents="none">
          <circle r="32" fill="none" stroke="#8C6239" strokeWidth="0.8" />
          <circle r="22" fill="none" stroke="#8C6239" strokeWidth="0.4" />
          <line x1="0" y1="-32" x2="0" y2="32" stroke="#8C6239" strokeWidth="0.6" />
          <line x1="-32" y1="0" x2="32" y2="0" stroke="#8C6239" strokeWidth="0.4" />
          <text x="0" y="-38" textAnchor="middle" fontSize="11"
                fontFamily="'Outfit', serif" fill="#8C6239"
                letterSpacing="0.18em">N</text>
        </g>

        {/* Centre pins */}
        {centres.map(c => {
          const [x, y] = projectLatLng(c.geo_lat, c.geo_lng,
                                        VIEW_W, VIEW_H, INDIA_BOUNDS);
          const isSelected = selected?.centre_id === c.centre_id;
          const intensity = c.stabilization_pulse?.intensity_pct || 30;
          // Ring radius: 30 (quiet) → 90 (flowing) px
          const ringR = 30 + (intensity / 100) * 60;
          const band = c.stabilization_pulse?.band || 'quiet';
          const calNeed = c.stabilization_pulse?.calibration_need;
          return (
            <g key={c.centre_id}
               data-testid={`map-pin-${c.centre_id}`}
               className="cursor-pointer"
               onClick={() => onSelect(c)}>
              <circle cx={x} cy={y} r={ringR}
                       fill={`url(#pulse-${band})`}
                       pointerEvents="none"
                       data-testid={`pulse-ring-${c.centre_id}`} />
              {calNeed && (
                <circle cx={x} cy={y} r={ringR + 4}
                         fill="none"
                         stroke="#C8A368" strokeOpacity="0.6"
                         strokeWidth="1" strokeDasharray="3 4"
                         pointerEvents="none"
                         data-testid={`calibration-ring-${c.centre_id}`} />
              )}
              {/* Transparent hit-target for reliable click */}
              <circle cx={x} cy={y} r={Math.max(ringR, 28)}
                       fill="transparent" />
              <circle cx={x} cy={y} r={isSelected ? 9 : 6}
                       fill={isSelected ? '#4A5D4E' : '#2C2A29'}
                       stroke="#FFFFFF" strokeWidth="2"
                       pointerEvents="none" />
              <text x={x} y={y - ringR - 8}
                     textAnchor="middle"
                     fontSize="14"
                     fontFamily="'Work Sans', sans-serif"
                     fill="#5C5955"
                     pointerEvents="none">
                {c.geo_label || c.centre_name}
              </text>
            </g>
          );
        })}
      </svg>
    </section>
  );
}

function CanvasLegend({ mapped, total }) {
  return (
    <div data-testid="map-legend"
         className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
        Stabilization Pulse™
      </p>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <PulseSwatch band="flowing"   label="Flowing" />
        <PulseSwatch band="stewarded" label="Stewarded" />
        <PulseSwatch band="softening" label="Softening" />
        <PulseSwatch band="quiet"     label="Quiet" />
        <span className="ml-auto text-[10px] tabular-nums text-[#8A8681]">
          {mapped} of {total} centres mapped
        </span>
      </div>
      <p className="text-[10px] text-[#8A8681] italic mt-3 max-w-md">
        Pulse encodes movement rhythm relative to stuck weight — wider rings
        signal a clearer calibration breath, not raw activity.
      </p>
    </div>
  );
}

const PULSE_TONE = {
  flowing:   '#4A5D4E',
  stewarded: '#7A8A8C',
  softening: '#C8A368',
  quiet:     '#A8A29E',
};

function PulseSwatch({ band, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-[#5C5955]">
      <span className="inline-block w-3 h-3 rounded-full"
             style={{ background: PULSE_TONE[band], opacity: 0.55 }} />
      {label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Drilldown panel
// ═══════════════════════════════════════════════════════════════════

function ViewerHint({ mapped }) {
  return (
    <article data-testid="map-viewer-hint"
             className="rounded-2xl border border-dashed border-[#D8D4CE] bg-white p-5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-2">
        Listen in
      </p>
      <p className="text-sm text-[#5C5955] leading-relaxed">
        {mapped > 0
          ? "Click a pin to hear that centre's stabilization breath."
          : "No centres are mapped yet. Check the Unmapped panel below."}
      </p>
    </article>
  );
}

function Drilldown({ centre, onClose }) {
  const tone = TONE_MAP[centre.posture?.tone] || TONE_MAP.stone;
  const rhythm = centre.movement_rhythm || {};
  const pulse  = centre.stabilization_pulse || {};
  const top    = centre.top_ecosystem_signal;
  return (
    <article data-testid={`drilldown-${centre.centre_id}`}
             className="rounded-2xl border border-[#EBE8E3] bg-white p-5 space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-base text-[#2C2A29] truncate">
            {centre.centre_name}
          </p>
          <p className="text-[11px] text-[#8A8681] mt-0.5 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {centre.geo_label || centre.city || '—'}
          </p>
        </div>
        <button data-testid="drilldown-close"
                 onClick={onClose}
                 className="text-[#8A8681] hover:text-[#2C2A29] -mt-1">
          <X className="w-4 h-4" />
        </button>
      </header>

      <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${tone}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
        {centre.posture?.label || centre.posture?.code}
      </div>
      <p className="text-[11px] text-[#5C5955] italic leading-relaxed">
        {centre.posture?.description}
      </p>

      <Row label="Maturity"
           value={centre.maturity_label || '—'} />
      <Row label="Right-subscriber emergence"
           value={centre.right_subscriber_emergence ?? 0} />
      <Row label="Forward movements"
           value={`${rhythm.forwards ?? 0} · Δ ${formatDelta(rhythm.delta_vs_prior)}`} />
      <Row label="Stabilization pulse"
           value={`${pulse.band || 'quiet'} · ${pulse.intensity_pct ?? 0}`} />
      {top && (
        <Row label={`Slowest ecosystem (${top.ecosystem_code})`}
             value={`${top.velocity_pct}% velocity`} />
      )}

      <div className="rounded-xl border border-[#E5D6BE] bg-[#FAF1E1]/40 p-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C6239] mb-1">
          Stabilization note
        </p>
        <p className="text-[12px] text-[#5C3D1F] leading-relaxed">
          {centre.stabilization_note}
        </p>
      </div>

      <div className="rounded-xl border border-[#EBE8E3] bg-[#FBFaf9] p-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-1">
          Suggested stewardship focus
        </p>
        <p className="text-[12px] text-[#2C2A29] leading-relaxed">
          {centre.suggested_focus}
        </p>
      </div>
    </article>
  );
}

const TONE_MAP = {
  emerald: 'border-emerald-200 bg-emerald-50/50 text-emerald-800',
  sky:     'border-sky-200 bg-sky-50/50 text-sky-800',
  amber:   'border-amber-200 bg-amber-50/50 text-amber-800',
  stone:   'border-stone-200 bg-stone-50/50 text-stone-700',
};

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12px]">
      <span className="text-[#8A8681]">{label}</span>
      <span className="text-[#2C2A29] tabular-nums truncate">{value}</span>
    </div>
  );
}

function formatDelta(d) {
  if (d == null) return '—';
  return d > 0 ? `+${d}` : `${d}`;
}

// ═══════════════════════════════════════════════════════════════════
// Unmapped panel
// ═══════════════════════════════════════════════════════════════════

function UnmappedPanel({ centres, onSelect, selected }) {
  if (!centres.length) return null;
  return (
    <article data-testid="unmapped-centres-panel"
             className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
        Unmapped centres
      </p>
      <p className="text-[11px] text-[#8A8681] italic mb-3 leading-relaxed">
        Coordinates are missing for these centres. They are still calmly held —
        listen to them by selecting from the list.
      </p>
      <ul className="space-y-1">
        {centres.map(c => (
          <li key={c.centre_id}>
            <button
              data-testid={`unmapped-${c.centre_id}`}
              onClick={() => onSelect(c)}
              className={`w-full text-left px-3 py-2 rounded-lg text-[12px] transition border ${
                selected?.centre_id === c.centre_id
                  ? 'bg-[#FBFaf9] border-[#D8D4CE]'
                  : 'bg-transparent border-transparent hover:bg-[#FBFaf9]'
              }`}>
              <span className="text-[#2C2A29]">{c.centre_name}</span>
              <span className="text-[#8A8681] ml-2">
                · {c.posture?.label || c.posture?.code}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </article>
  );
}
